import React from "react";
import {
  MdClose,
  MdStar,
  MdStarBorder,
  MdVisibility,
  MdVisibilityOff,
  MdMessage,
  MdCalendarToday,
  MdPerson,
  MdShoppingBag,
  MdReply,
} from "react-icons/md";

const ReviewDetailsModal = ({
  review,
  onClose,
  //   reviewVisibility,
  onToggleVisibility,
}) => {
  if (!review) return null;

  const user = review?.user;
  const reviewImages = Array.isArray(review?.images) ? review.images : [];
  const product = review?.product_detail;
  const isHidden = review.isHide === true;
  const isVisible = !isHidden;

  // Helper to render filled stars
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) =>
      i < rating ? (
        <MdStar key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
      ) : (
        <MdStarBorder key={i} className="w-5 h-5 text-gray-300" />
      )
    );
  };
  //   console.log("reviewVisibility:",reviewVisibility)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        {/* Sticky Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-5 flex justify-between items-center rounded-t-2xl z-10">
          <h2 className="text-2xl font-bold text-gray-900">Review Details</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <MdClose className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        <div className="p-8 space-y-8">
          {/* Review Section */}
          <section>
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
              <MdMessage className="w-5 h-5 text-blue-600" />
              Customer Review
            </h3>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex">{renderStars(review.rating)}</div>
              <span className="text-sm font-medium text-gray-700">
                {review.rating}.0 out of 5 stars
              </span>
            </div>
            {/* Review Images */}
            {reviewImages.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  {`Review Images (${reviewImages.length})`}
                </h4>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {reviewImages.map((img, index) => (
                    <div
                      key={index}
                      className="group relative overflow-hidden rounded-xl border bg-gray-100 cursor-pointer"
                    >
                      <img
                        src={img}
                        alt={`Review image ${index + 1}`}
                        className="w-full h-32 object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/150?text=Image";
                        }}
                      />

                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Review Text */}
            <p className="text-gray-700 leading-relaxed text-base whitespace-pre-line bg-gray-50 p-5 rounded-xl border">
              {review.review || (
                <span className="text-gray-400 italic">No written review</span>
              )}
            </p>

            {/* Vendor Reply */}
            {review?.vendor_reply && (
              <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
                <p className="font-semibold text-blue-900 flex items-center gap-2">
                  <MdReply className="w-5 h-5" />
                  Your Reply
                </p>
                <p className="mt-3 text-gray-800 leading-relaxed">
                  {review.vendor_reply}
                </p>
              </div>
            )}

            {/* Date */}
            <div className="flex items-center gap-2 text-sm text-gray-500 mt-5">
              <MdCalendarToday className="w-4 h-4" />
              {new Date(review.createdAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </section>

          {/* Visibility Toggle */}
          <div className="flex items-center justify-between p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border">
            <div className="flex items-center gap-4">
              {isVisible ? (
                <MdVisibility className="w-6 h-6 text-green-600" />
              ) : (
                <MdVisibilityOff className="w-6 h-6 text-red-500" />
              )}

              <div>
                <p className="font-semibold text-gray-800">
                  {isVisible ? "Visible to Public" : "Hidden from Public"}
                </p>

                <p className="text-sm text-gray-600">
                  {isVisible
                    ? "Customers can see this review"
                    : "This review is hidden"}
                </p>
              </div>
            </div>

            <button
              onClick={onToggleVisibility}
              className={`relative inline-flex h-11 w-20 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-blue-300 ${
                isVisible ? "bg-blue-600" : "bg-gray-400"
              }`}
              aria-label={isVisible ? "Hide review" : "Show review"}
            >
              <span
                className={`inline-block h-9 w-9 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
                  isVisible ? "translate-x-10" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* User & Product Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* User Card */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
                <MdPerson className="w-6 h-6 text-indigo-600" />
                Customer
              </h3>
              <div className="flex items-center gap-4">
                <img
                  src={user?.profile_image || "https://via.placeholder.com/150"}
                  alt="User"
                  className="w-16 h-16 rounded-full object-cover ring-4 ring-white shadow-md"
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/150?text=User";
                  }}
                />
                <div>
                  <p className="font-semibold text-gray-900">
                    {user?.first_name || "Anonymous"}{" "}
                    {user?.last_name || "User"}
                  </p>
                  <p className="text-sm text-gray-600">
                    {user?.email || "N/A"}
                  </p>
                  {user?.phone_number && (
                    <p className="text-sm text-gray-600">{user.phone_number}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Product Card */}
            {product && (
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
                  <MdShoppingBag className="w-6 h-6 text-emerald-600" />
                  Product Reviewed
                </h3>
                <div className="flex items-center gap-4">
                  <img
                    src={
                      product.primary_image || "https://via.placeholder.com/150"
                    }
                    alt={product.name}
                    className="w-16 h-16 rounded-lg object-cover ring-4 ring-white shadow-md"
                  />
                  <div>
                    <p className="font-semibold text-gray-900 line-clamp-2">
                      {product.name}
                    </p>
                    <p className="text-sm text-gray-600">
                      Brand: {product.brand || "N/A"}
                    </p>
                    {product.model && (
                      <p className="text-sm text-gray-600">
                        Model: {product.model}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewDetailsModal;
